import type { FC } from 'react';
import cx from 'classnames';
import { CalendarIcon, ChecklistIcon, ClockIcon, InspectIcon, PaperIcon } from '@THC/components/Icons';
import { MDText } from 'i18n-react';

import { DealershipIcon, SpeedometerIcon } from '@motorway/mw-highway-code';

import type { VehicleData } from 'Pages/vehicles/helpers/vehicle.schema';
import { formatShortDate } from 'Utilities/formatters';
import { numberWithCommas } from 'Utilities/helpers';

import LocalTexts from '../ServiceHistoryEnhancedSection.texts.json';

import { ServiceHistoryInfoCard } from './ServiceHistoryInfoCard';

import styles from './ServiceHistoryInfoCards.module.scss';

const LocalT = new MDText(LocalTexts);

export const serviceHistoryMap = {
	'-': LocalT.translate('none'),
	full: LocalT.translate('serviceTypes.full'),
	no: LocalT.translate('serviceTypes.no'),
	not_applicable: LocalT.translate('serviceTypes.notApplicable'),
	partial: LocalT.translate('serviceTypes.partial'),
	skipped: LocalT.translate('none'),
	yes: LocalT.translate('serviceTypes.yes'),
};

export const serviceCentre = {
	'': LocalT.translate('none'),
	I: LocalT.translate('serviceCentre.independent'),
	M: LocalT.translate('serviceCentre.main'),
	MI: LocalT.translate('serviceCentre.mixed'),
};

interface ServiceHistoryInfoCardsProps {
	distanceInterval: VehicleData['serviceHistory']['distanceInterval'];
	hasNoServiceHistory?: boolean;
	independentServiceStampsCount: VehicleData['serviceHistory']['independentServiceStampsCount'];
	isNewishVehicle: boolean;
	mileage: number;
	officialServiceStampsCount: VehicleData['serviceHistory']['officialServiceStampsCount'];
	serviceRecord: VehicleData['serviceHistory']['serviceRecord'];
	serviceRecords: VehicleData['serviceHistory']['serviceHistoryRecords'];
	shouldHideDetailedServiceHistoryCards?: boolean;
	timeInterval: VehicleData['serviceHistory']['timeInterval'];
	vehicleAge: number;
}

export const ServiceHistoryInfoCards: FC<ServiceHistoryInfoCardsProps> = ({
	distanceInterval,
	hasNoServiceHistory,
	isNewishVehicle,
	mileage,
	serviceRecord,
	serviceRecords,
	shouldHideDetailedServiceHistoryCards,
	timeInterval,
	vehicleAge,
}) => {
	const isServiceCenterVisible = !hasNoServiceHistory && !!serviceRecords?.count && isNewishVehicle;
	const isLastServiceVisible = !!serviceRecords?.count && !isNewishVehicle;
	const hasEntries = !hasNoServiceHistory;
	const mostRecentRecord = serviceRecords?.items[0];
	const hasIndependentServices = serviceRecords?.items.some(({ dealershipType }) => dealershipType === 'independent');
	const hasMainServices = serviceRecords?.items.some(({ dealershipType }) => dealershipType === 'main');
	const isIntervalVisible =
		!shouldHideDetailedServiceHistoryCards && !hasNoServiceHistory && distanceInterval && timeInterval;

	return (
		<>
			<div className={styles.container}>
				<ServiceHistoryInfoCard icon={<PaperIcon />} text={LocalT.translate('info.serviceHistory')}>
					{serviceHistoryMap[serviceRecord]}
				</ServiceHistoryInfoCard>
				{!shouldHideDetailedServiceHistoryCards && (
					<>
						<ServiceHistoryInfoCard filled={false} icon={<ClockIcon />} text={LocalT.translate('info.vehicleAge')}>
							{LocalT.translate('info.vehicleAgeValue', {
								pluralise: vehicleAge === 1 ? '' : 's',
								vehicleAge,
							})}
						</ServiceHistoryInfoCard>
						<ServiceHistoryInfoCard
							icon={<InspectIcon />}
							isHidden={!hasEntries}
							text={LocalT.translate('info.serviceEntries')}
						>
							{serviceRecords?.items.length || '-'}
						</ServiceHistoryInfoCard>
						<ServiceHistoryInfoCard
							icon={<SpeedometerIcon />}
							isHidden={!hasNoServiceHistory}
							text={LocalT.translate('info.mileage')}
						>
							{numberWithCommas(mileage)}
						</ServiceHistoryInfoCard>
						<ServiceHistoryInfoCard
							icon={<CalendarIcon />}
							isHidden={!isLastServiceVisible}
							text={LocalT.translate('info.lastService')}
						>
							<span className={styles.mobile}>{formatShortDate(mostRecentRecord?.date)}</span>
						</ServiceHistoryInfoCard>
						<ServiceHistoryInfoCard
							icon={<DealershipIcon />}
							isHidden={!isServiceCenterVisible}
							text={LocalT.translate('info.serviceCentres')}
						>
							{serviceCentre[`${hasMainServices ? 'M' : ''}${hasIndependentServices ? 'I' : ''}`]}
						</ServiceHistoryInfoCard>
					</>
				)}
			</div>
			{isIntervalVisible && (
				<div className={cx(styles.container, styles.interval)}>
					<ServiceHistoryInfoCard icon={<ChecklistIcon />} text={LocalT.translate('info.serviceIntervals')}>
						{LocalT.translate('info.serviceIntervalValue', {
							distance: numberWithCommas(distanceInterval || 0),
							pluralise: timeInterval === 1 ? '' : 's',
							time: timeInterval,
						})}
					</ServiceHistoryInfoCard>
				</div>
			)}
		</>
	);
};
